import { useState, useEffect, useRef } from 'react';
import { reportCenterService, propertyService, assemblyService, ownerService } from 'services/api';
import styles from './CuentasPropietarios.module.css';

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useCuentasPropietarios() {
  const [properties, setProperties] = useState([]);
  const [propSel, setPropSel] = useState(null); // conjunto elegido en el buscador (puede no estar en `properties`)
  const [assemblies, setAssemblies] = useState([]);
  const [propId,     setPropId]     = useState('');
  const [asmId,      setAsmId]      = useState('');

  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [alert,    setAlert]    = useState(null);
  const [q,        setQ]        = useState('');
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [soloRebotados, setSoloRebotados] = useState(false);

  // Paginación (cliente — el endpoint devuelve todas las filas)
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Selección para el reenvío del correo de activación
  const [selected, setSelected] = useState(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [sending,  setSending]  = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    propertyService.selector({ limit: 200 }).then(r => {
      const props = r.data.data || [];
      setProperties(props);
      if (props.length === 1) setPropId(String(props[0].id));
    });
    assemblyService.getAll({ limit: 200 }).then(r => setAssemblies(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!propId) { setData(null); return; }
    setLoading(true); setAlert(null); setSelected(new Set());
    const params = { property_id: propId };
    if (asmId) params.assembly_id = asmId;
    reportCenterService.getPadron(params)
      .then(r => setData(r.data))
      .catch(err => { setData(null); setAlert({ type: 'danger', message: err.response?.data?.message || 'No se pudieron cargar las cuentas.' }); })
      .finally(() => setLoading(false));
  }, [propId, asmId]);

  // Cerrar el menú de reenvío al hacer click fuera
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const propAssemblies = assemblies.filter(a => String(a.property?.id) === String(propId));
  const conAsamblea = !!data?.assembly;

  const download = async () => {
    if (!propId) return;
    setDownloading(true);
    try {
      const params = { property_id: propId };
      if (asmId) params.assembly_id = asmId;
      const r = await reportCenterService.downloadPadron(params);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      const prop = (propSel && String(propSel.id) === String(propId)) ? propSel : properties.find(p => String(p.id) === String(propId));
      a.href = url;
      a.download = `Cuentas_propietarios_${(prop?.name || 'propiedad').replace(/\s+/g, '_')}${asmId ? '_asamblea' : ''}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAlert({ type: 'danger', message: 'No se pudo descargar el Excel.' });
    } finally { setDownloading(false); }
  };

  const allRows = data?.rows || [];
  const rows = allRows.filter(r => {
    if (soloRebotados && !r.rebotado) return false;
    if (soloProblemas && r.cuenta_activada && r.tiene_telefono) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return r.propietario.toLowerCase().includes(t) || r.documento.toLowerCase().includes(t)
      || r.correo.toLowerCase().includes(t) || r.unidades.toLowerCase().includes(t);
  });

  // Volver a la página 1 cuando cambian los filtros o la propiedad/asamblea
  useEffect(() => { setPage(1); }, [q, soloProblemas, soloRebotados, propId, asmId, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pageRows   = rows.slice(pageStart, pageStart + pageSize);
  const irAPagina  = (n) => { setPage(n); window.scrollTo({ top: 0 }); };

  // Conteos para las opciones de reenvío (sobre TODA la propiedad, no el filtro)
  const conCorreo   = allRows.filter(r => r.correo).length;
  const sinActivar   = allRows.filter(r => r.correo && !r.cuenta_activada).length;
  // "Seleccionar visibles" opera sobre la página actual
  const seleccionables = pageRows.filter(r => r.correo && r.owner_id);
  const visiblesSelIds = seleccionables.map(r => r.owner_id);
  const allVisibleSelected = visiblesSelIds.length > 0 && visiblesSelIds.every(id => selected.has(id));

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllVisible = () => setSelected(prev => {
    const next = new Set(prev);
    if (allVisibleSelected) visiblesSelIds.forEach(id => next.delete(id));
    else visiblesSelIds.forEach(id => next.add(id));
    return next;
  });

  const resend = async (scope) => {
    setMenuOpen(false);
    setSending(true); setAlert(null);
    try {
      const payload = { property_id: propId, scope };
      if (scope === 'selected') payload.owner_ids = [...selected];
      const r = await ownerService.resendWelcome(payload);
      setAlert({ type: r.data.total ? 'success' : 'info', message: r.data.message });
      if (scope === 'selected') setSelected(new Set());
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'No se pudo iniciar el reenvío.' });
    } finally { setSending(false); }
  };

  const Pill = ({ ok, si, no }) => (
    <span className={ok ? styles.pillSi : styles.pillNo}>
      <i className={`fas ${ok ? 'fa-circle-check' : 'fa-circle-xmark'} ${styles.pillGlifo}`} />
      {ok ? si : no}
    </span>
  );

  return {
    setPropSel, propId, setPropId, asmId, setAsmId, data, loading, downloading, alert, setAlert, q,
    setQ, soloProblemas, setSoloProblemas, soloRebotados, setSoloRebotados, pageSize, setPageSize,
    selected, setSelected, menuOpen, setMenuOpen, sending, menuRef, propAssemblies, conAsamblea,
    download, rows, safePage, pageRows, irAPagina, conCorreo, sinActivar, seleccionables,
    allVisibleSelected, toggleOne, toggleAllVisible, resend, Pill,
  };
}
